import { useEffect, useRef, useState } from "react";
import { HiDotsVertical } from "react-icons/hi";
import { FiCopy, FiEdit3, FiFileText, FiTrash2, FiEye } from "react-icons/fi";
import { useJwt } from "../../../../../helpers/common";
import { useOrganizationService, useProjectService, useRoadmapService } from "../../../../../services";
import DrawerTemplates from "../../../../Templates/AddUpdateTemplate";
import Portal from "../../../../../components/Portal";
import ProjectDeleteModal from "../../../../../components/Modals/ProjectDeleteModal";
import RoadmapDeleteModal from "../../../../../components/Modals/RoadmapDeleteModal";
import AddUpdateProjectModal from "../../AddUpdateProjectModal";
import CreateAdvancedEpicModal from "../../../../../features/AdvancedEpic/CreateAdvancedEpicModal";
import { setSingleRoadmap } from "../../../../../redux/slices/roadmap";
import { useDispatch, useSelector } from "react-redux";
import { PinIcon, PinOff } from "lucide-react";
import { ReduxStateType } from "../../../../../redux/store";

interface WorkItemOptionsProps {
    data: any;
    onViewDetails?: () => void;
    className?: string;
    showOnHover?: boolean;
    hideCopy?: boolean;
    hideEdit?: boolean;
    onRefresh?: () => void;
    source?: string;
}

/**
 * WorkItemOptions - Options menu for both roadmaps (Epic Advance) and projects
 * 
 * For Roadmaps (Epic Advance):
 * - Shows: View, Edit, Delete
 * - Edit opens CreateRoadMapsModal
 * - Delete uses RoadmapDeleteModal
 * 
 * For Projects (Epic/Feature/etc):
 * - Shows: View, Edit, Copy, Create template, Delete
 * - Edit opens AddUpdateProjectModal
 * - Delete uses ProjectDeleteModal
 */
const WorkItemOptions = ({
    data = {},
    onViewDetails,
    className = "",
    showOnHover = false,
    hideCopy = false,
    hideEdit = false,
    onRefresh,
    source
}: WorkItemOptionsProps) => {
    const dispatch = useDispatch();
    const [showListGroup, setShowListGroup] = useState(false);
    const [showConfirmModal, setConfirmModal] = useState(false);
    const [toggleCreateModal, setToggleCreateModal] = useState(false);
    const [payloadData, setPayloadData] = useState({});
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showRoadmapModal, setShowRoadmapModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState<any>({});
    const [selectedRoadmap, setSelectedRoadmap] = useState<any>({});

    // Delete state for roadmaps
    const [isDeleting, setIsDeleting] = useState(false);

    
    const { copyProject, deleteProjects } = useProjectService();
    const { deleteRoadmap } = useRoadmapService();
    const {
      fetchRoadmapsAndProjects,
      fetchPinnedItems,
    } = useOrganizationService();
    const JWTdata: any = useJwt();
    const org_id = JWTdata.org_id;

    // Get singleRoadmapRes from Redux for the roadmap modal
    const { singleRoadmapRes } = useSelector((state: any) => state.roadmap);

    // Get current workItems filters from Redux to preserve them when reloading
    const {
        workItemsSearchTerm,
        workItemsSearchType,
        workItemsSortBy,
        workItemsSourceFilter,
        workItemsQuarterFilter = [],
        workItemsPinnedFilter = false,
    } = useSelector((state: any) => state.organization);

    // Check if item is a roadmap (Epic Advance) - roadmaps only show View, Edit, Delete
    const isRoadmap = data?.type === 'roadmap';

    useEffect(() => {
        if (showListGroup && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const dropdownWidth = 180;
            // More accurate height: 36px per item + 8px padding top/bottom
            const itemCount = isRoadmap ? 3 : 5; // View, Edit, Delete for roadmap; View, Edit, Copy, Template, Delete for project
            const dropdownHeight = (itemCount * 36) + 16;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const spaceBelow = viewportHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

            // Position horizontally: prefer right-aligned to button, but don't go off screen
            let leftPos = buttonRect.right - dropdownWidth;
            if (leftPos < 8) {
                leftPos = 8; // Minimum 8px from left edge
            }
            if (leftPos + dropdownWidth > viewportWidth - 8) {
                leftPos = viewportWidth - dropdownWidth - 8;
            }
            setDropdownPosition({
                top: shouldPositionAbove
                    ? buttonRect.top - dropdownHeight
                    : buttonRect.bottom + 2,
                left: leftPos,
            });
        }
    }, [showListGroup, isRoadmap]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showListGroup && buttonRef.current) {
                const target = event.target as Node;
                // Check if click is inside button or dropdown (via ref)
                if (!buttonRef.current.contains(target) &&
                    (!dropdownRef.current || !dropdownRef.current.contains(target))) {
                    setShowListGroup(false);
                }
            }
        };

        const handleScroll = () => {
            if (showListGroup) {
                setShowListGroup(false);
            }
        };

        const handleResize = () => {
            if (showListGroup) {
                setShowListGroup(false);
            }
        };

        if (showListGroup) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [showListGroup]);

    const handleListClick = async (type: string) => {
        setShowListGroup(false);

       
          if (type === "copy") {
            // Copy only applies to projects
            // Transform data to include project_id (combined API uses id)
            const copyData = {
              ...data,
              project_id: data.id || data.project_id,
            };
            copyProject(org_id, copyData).then(() => {
              // Refresh with current filters preserved
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
              fetchPinnedItems();
              onRefresh?.();
            });
          }

        if (type === "editproject") {
            if (isRoadmap) {
                // Epic Advance - use CreateRoadMapsModal
                // Don't fetch here - the modal will fetch via its own useEffect
                setSelectedRoadmap({ id: data.id, ...data });
                setShowRoadmapModal(true);
            } else {
                // Normal project - use AddUpdateProjectModal
                // Transform CombinedItem to match what AddUpdateProjectModal expects
                const projectData = {
                    ...data,
                    project_id: data.id ?? data.project_id,
                    project_name: data.name ?? data.project_name,
                    project_description: data.description ?? data.project_description,
                    project_color: data.color ?? data.project_color,
                };
                setSelectedProject(projectData);
                setShowProjectModal(true);
            }
        }

        if (type === "createtemplate") {
            // Combined API uses id, template expects project_id
            setPayloadData({ project_id: data.id || data.project_id });
            setToggleCreateModal(true);
        }

        if (type === "deleteProject") {
            setConfirmModal(true);
        }

        if (type === "viewDetails") {
            onViewDetails?.();
        }
    };

    const handleConfirmDelete = async () => {
        if (isRoadmap) {
            // Delete roadmap - wait for API before closing modal
            try {
                setIsDeleting(true);
                await deleteRoadmap(data.id);
                setConfirmModal(false); // Close modal after successful delete
                // Refresh with current filters preserved
                fetchRoadmapsAndProjects(true, undefined, workItemsSearchTerm, workItemsSearchType, workItemsSortBy, workItemsSourceFilter, workItemsQuarterFilter, workItemsPinnedFilter);
                fetchPinnedItems();
                onRefresh?.();
            } catch (error) {
                console.error('Error deleting roadmap:', error);
                setConfirmModal(false); // Close modal on error too
            } finally {
                setIsDeleting(false);
            }
        } else {
            // Delete project - wait for API before closing modal
            setIsDeleting(true);
            const projectId = data.id || data.project_id;
            deleteProjects(projectId, org_id)
                .then(() => {
                    setConfirmModal(false); // Close modal after successful delete
                    // Refresh with current filters preserved
                    fetchRoadmapsAndProjects(true, undefined, workItemsSearchTerm, workItemsSearchType, workItemsSortBy, workItemsSourceFilter, workItemsQuarterFilter, workItemsPinnedFilter);
                    fetchPinnedItems();
                    onRefresh?.();
                })
                .catch((error) => {
                    console.error('Error deleting project:', error);
                    setConfirmModal(false); // Close modal on error too
                })
                .finally(() => {
                    setIsDeleting(false);
                });
        }
    };

    const handleRoadmapModalClose = (val: boolean) => {
        setShowRoadmapModal(val);
        if (!val) {
            setSelectedRoadmap({});
            dispatch(setSingleRoadmap({}));
            // Don't refresh here - only refresh when there's an actual update
        }
    };

    const handleProjectModalClose = (val: boolean) => {
        setShowProjectModal(val);
        if (!val) {
            setSelectedProject({});
            // Don't refresh here - only refresh when there's an actual update
        }
    };

    const menuItems = [
        ...(!hideEdit ? [{
            id: "editproject",
            label: "Edit",
            icon: FiEdit3,
            className: "text-light-text-primary hover:text-primary-light hover:bg-light-interactive-hover"
        }] : []),
    
        // Hide Copy for roadmaps (Epic Advance)
        ...(!hideCopy && !isRoadmap ? [{
            id: "copy",
            label: "Make a Copy",
            icon: FiCopy,
            className: "text-light-text-primary hover:text-primary-light hover:bg-light-interactive-hover"
        }] : []),
        // Hide Create template for roadmaps (Epic Advance)
        ...(!isRoadmap ? [{
            id: "createtemplate",
            label: "Create template",
            icon: FiFileText,
            className: "text-light-text-primary hover:text-primary-light hover:bg-light-interactive-hover"
        }] : []),
        {
            id: "deleteProject",
            label: "Delete",
            icon: FiTrash2,
            className: "text-light-status-error-text hover:text-red-600 hover:bg-red-50"
        }
    ];

    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setShowListGroup(false);
        }, 300);
    };

    return (
        <>
            <div className={`${showOnHover ? '' : 'px-4 py-2'} font-medium relative ${className}`}>
                <button
                    ref={buttonRef}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowListGroup(true);
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    type="button"
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${showOnHover ? (showListGroup ? 'flex' : 'group-hover:flex hidden') : 'flex'
                        }`}
                    title="More actions"
                >
                    <HiDotsVertical className="w-4 h-4" />
                </button>

                {showListGroup && (
                    <Portal>
                        <div
                            ref={dropdownRef}
                            data-dropdown="workitem-options-menu"
                            className="fixed min-w-[180px] rounded-lg border border-light-border-default dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden z-[99999]"
                            style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="py-2">
                                {menuItems.map((item) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleListClick(item.id);
                                            }}
                                            className={`w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm transition-colors duration-150 ${item.id === 'deleteProject'
                                                ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20'
                                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            <span className="text-left leading-tight">{item.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </Portal>
                )}
            </div>

            {/* Create Template Drawer */}
            {toggleCreateModal && (
                <Portal>
                    <DrawerTemplates
                        toggleCreateModal={toggleCreateModal}
                        setToggleCreateModal={setToggleCreateModal}
                        payloadData={payloadData}
                        setPayloadData={setPayloadData}
                    />
                </Portal>
            )}

            {/* Delete Confirmation Modal - Project */}
            {showConfirmModal && !isRoadmap && (
                <Portal>
                    <ProjectDeleteModal
                        setShowModal={setConfirmModal}
                        showModal={showConfirmModal}
                        handleConfirm={handleConfirmDelete}
                        projectName={data?.name || data?.project_name}
                        isDeleting={isDeleting}
                    />
                </Portal>
            )}

            {/* Delete Confirmation Modal - Roadmap */}
            {showConfirmModal && isRoadmap && (
                <Portal>
                    <RoadmapDeleteModal
                        setShowModal={setConfirmModal}
                        showModal={showConfirmModal}
                        handleConfirm={handleConfirmDelete}
                        projectName={data?.name}
                        isDeleting={isDeleting}
                    />
                </Portal>
            )}

            {/* Edit Project Modal */}
            {showProjectModal && (
                <Portal>
                    <AddUpdateProjectModal
                        isVisible={showProjectModal}
                        setShowModal={handleProjectModalClose}
                        project={selectedProject}
                        onProjectUpdate={() => {
                            // Only refresh when there's an actual update - preserve filters
                            fetchRoadmapsAndProjects(true, undefined, workItemsSearchTerm, workItemsSearchType, workItemsSortBy, workItemsSourceFilter, workItemsQuarterFilter, workItemsPinnedFilter);
                            fetchPinnedItems();
                            onRefresh?.();
                        }}
                    />
                </Portal>
            )}

                <Portal>
                    <CreateAdvancedEpicModal
                        isVisible={showRoadmapModal}
                        setShowModal={handleRoadmapModalClose}
                        roadmap={selectedRoadmap}
                        setSelectedRoadmap={async () => {
                            // Only refresh when there's an actual update - preserve filters
                            fetchRoadmapsAndProjects(true, undefined, workItemsSearchTerm, workItemsSearchType, workItemsSortBy, workItemsSourceFilter, workItemsQuarterFilter, workItemsPinnedFilter);
                            fetchPinnedItems();
                            onRefresh?.();
                        }}
                    />
                </Portal>
        </>
    );
};

export default WorkItemOptions;
